import jsPDF from 'jspdf';
import type { ChatMessage } from './types';
import { formatChatConversationStartedAt } from '../../../helpers/dateHelper';

interface ChatTranscriptResource {
  title: string;
  description: string;
  url?: string;
  imgUrl?: string;
}

interface ExportChatTranscriptOptions {
  messages: ChatMessage[];
  conversationStartedAt?: string | null;
  shouldDownload?: boolean;
  shouldPrint?: boolean;
}

type ResourceImageCache = Map<string, string>;

const PAGE_MARGIN = 16;
const HEADER_GAP = 3;
const LINE_HEIGHT = 6;
const MESSAGE_GAP = 0;
const DEFAULT_FILE_NAME = 'chat-transcript.pdf';
const PDF_TITLE_DATE_FORMAT = 'h:mm a DD/MM/YY';
const PDF_SUBTITLE_DATE_FORMAT = 'DD/MM/YYYY hh:mm a';
const ASSISTANT_NAME_FALLBACK = 'EndoZoneAI';

const RESOURCE_CARD_PADDING_H = 4;
const RESOURCE_CARD_PADDING_V = 4;
const RESOURCE_ACCENT_WIDTH = 1.6;
const RESOURCE_THUMB_SIZE = 28;
const RESOURCE_THUMB_GAP = 5;
const RESOURCE_THUMB_BORDER_WIDTH = 0.4;
const RESOURCE_TITLE_GAP = 2;
const RESOURCE_DESCRIPTION_GAP = 2;
const RESOURCE_SOURCE_GAP = 2;
const RESOURCE_LABEL_GAP = 1;
const RESOURCE_LINK_UNDERLINE_OFFSET = 0.6;

const COLOR_CARD_BG: [number, number, number] = [245, 250, 253];
const COLOR_CARD_BORDER: [number, number, number] = [212, 223, 228];
const COLOR_CARD_ACCENT: [number, number, number] = [77, 153, 204];
const COLOR_THUMB_BG: [number, number, number] = [255, 255, 255];
const COLOR_THUMB_BORDER: [number, number, number] = [224, 227, 230];
const COLOR_TITLE: [number, number, number] = [31, 41, 55];
const COLOR_DESCRIPTION: [number, number, number] = [92, 100, 105];
const COLOR_SOURCE_LABEL: [number, number, number] = [92, 100, 105];
const COLOR_SOURCE_LINK: [number, number, number] = [27, 95, 173];

const normalizeMessageText = (text: string): string => {
  if (!text) return '';
  let normalized = text.replace(/\r\n/g, '\n');
  normalized = normalized.replace(/[ \t]+$/gm, '');
  normalized = normalized.replace(/\n{3,}/g, '\n\n');
  normalized = normalized.trim();
  return normalized;
};

const getAssistantName = (message: ChatMessage) => {
  if (typeof message.data?.authorName === 'string' && message.data.authorName) {
    return message.data.authorName;
  }

  return ASSISTANT_NAME_FALLBACK;
};

const getMessageResources = (
  message: ChatMessage,
): ChatTranscriptResource[] => {
  if (!Array.isArray(message.data?.endoPatientResources)) {
    return [];
  }

  return message.data.endoPatientResources.filter(
    (item): item is ChatTranscriptResource =>
      Boolean(item) &&
      typeof item.title === 'string' &&
      typeof item.description === 'string',
  );
};

const collectAllResources = (messages: ChatMessage[]) => {
  const resources: ChatTranscriptResource[] = [];
  messages.forEach((message) => {
    resources.push(...getMessageResources(message));
  });
  return resources;
};

const createFileName = () => {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return `chat-transcript-${timestamp}.pdf`;
};

const imageToDataURL = (imgUrl: string): Promise<string> =>
  new Promise((resolve) => {
    if (!imgUrl) {
      resolve('');
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.referrerPolicy = 'no-referrer';

    const finalize = () => {
      try {
        const width = Math.max(
          1,
          Math.round(img.naturalWidth || img.width || 1),
        );
        const height = Math.max(
          1,
          Math.round(img.naturalHeight || img.height || 1),
        );
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve('');
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve('');
      }
    };

    img.onload = () => {
      window.setTimeout(finalize, 0);
    };
    img.onerror = () => {
      resolve('');
    };

    try {
      img.src = imgUrl;
      if (img.complete && img.naturalWidth > 0) {
        window.setTimeout(finalize, 0);
      }
    } catch {
      resolve('');
    }
  });

const preloadResourceImages = async (
  resources: ChatTranscriptResource[],
): Promise<ResourceImageCache> => {
  const urls = Array.from(
    new Set(
      resources
        .map((item) => (typeof item.imgUrl === 'string' ? item.imgUrl : ''))
        .filter(Boolean),
    ),
  );
  const dataUrls = await Promise.all(urls.map((url) => imageToDataURL(url)));
  const cache: ResourceImageCache = new Map();
  urls.forEach((url, index) => {
    cache.set(url, dataUrls[index] || '');
  });
  return cache;
};

const ensurePageSpace = (
  pdf: jsPDF,
  currentY: number,
  requiredHeight: number,
) => {
  const pageHeight = pdf.internal.pageSize.getHeight();
  if (currentY + requiredHeight <= pageHeight - PAGE_MARGIN) {
    return currentY;
  }

  pdf.addPage();
  return PAGE_MARGIN;
};

const addWrappedText = (
  pdf: jsPDF,
  text: string,
  currentY: number,
  options?: {
    fontSize?: number;
    fontStyle?: 'normal' | 'bold';
    textColor?: [number, number, number];
    maxWidth?: number;
    startX?: number;
    lineHeightScale?: number;
  },
) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const contentWidth = options?.maxWidth ?? pageWidth - PAGE_MARGIN * 2;
  const startX = options?.startX ?? PAGE_MARGIN;
  const normalizedText = text.replace(/\r\n/g, '\n').trim();
  const lines = normalizedText
    ? pdf.splitTextToSize(normalizedText, contentWidth)
    : [''];
  const fontSize = options?.fontSize ?? 11;
  const lineHeightBase = fontSize >= 14 ? LINE_HEIGHT + 1 : LINE_HEIGHT;
  const lineHeight = lineHeightBase * (options?.lineHeightScale ?? 1);
  const textHeight = Math.max(lines.length, 1) * lineHeight;
  const nextY = ensurePageSpace(pdf, currentY, textHeight);

  pdf.setFont('helvetica', options?.fontStyle ?? 'normal');
  pdf.setFontSize(fontSize);
  pdf.setTextColor(...(options?.textColor ?? [51, 51, 51]));
  pdf.text(lines, startX, nextY);

  return nextY + textHeight;
};

const addResourceCard = (
  pdf: jsPDF,
  resource: ChatTranscriptResource,
  currentY: number,
  imageCache: ResourceImageCache,
) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const contentWidth = pageWidth - PAGE_MARGIN * 2;
  const thumbDataUrl =
    typeof resource.imgUrl === 'string' && resource.imgUrl.trim()
      ? (imageCache.get(resource.imgUrl) ?? '')
      : '';
  const hasThumb = Boolean(thumbDataUrl);

  const cardInnerStartX =
    PAGE_MARGIN + RESOURCE_ACCENT_WIDTH + RESOURCE_CARD_PADDING_H;
  const cardInnerWidth =
    contentWidth - RESOURCE_ACCENT_WIDTH - RESOURCE_CARD_PADDING_H * 2;

  const textStartX = hasThumb
    ? cardInnerStartX + RESOURCE_THUMB_SIZE + RESOURCE_THUMB_GAP
    : cardInnerStartX;
  const textMaxWidth = hasThumb
    ? cardInnerWidth - RESOURCE_THUMB_SIZE - RESOURCE_THUMB_GAP
    : cardInnerWidth;

  const titleFontSize = 14;
  const titleLineHeight = LINE_HEIGHT + 1.4;
  const descriptionFontSize = 12;
  const descriptionLineHeight = LINE_HEIGHT + 1.2;
  const sourceFontSize = 12;
  const sourceLineHeight = LINE_HEIGHT + 1.2;

  const titleLines = pdf.splitTextToSize(
    resource.title.trim() || 'Untitled resource',
    textMaxWidth,
  );
  const titleHeight = titleLines.length * titleLineHeight;

  const descriptionText = resource.description.trim();
  const descriptionLines = descriptionText
    ? pdf.splitTextToSize(descriptionText, textMaxWidth)
    : [];
  const descriptionHeight =
    descriptionLines.length > 0
      ? descriptionLines.length * descriptionLineHeight +
        RESOURCE_DESCRIPTION_GAP
      : 0;

  const sourceFullText =
    typeof resource.url === 'string' && resource.url.trim()
      ? `Source: ${resource.url.trim()}`
      : '';
  const sourceLines = sourceFullText
    ? pdf.splitTextToSize(sourceFullText, textMaxWidth)
    : [];
  const sourceHeight =
    sourceLines.length > 0 ? sourceLines.length * sourceLineHeight : 0;

  const thumbTotalHeight = hasThumb ? RESOURCE_THUMB_SIZE : 0;

  const textTotalHeight =
    RESOURCE_SOURCE_GAP +
    titleHeight +
    RESOURCE_TITLE_GAP +
    descriptionHeight +
    sourceHeight;

  const cardInnerHeight = Math.max(
    textTotalHeight + RESOURCE_CARD_PADDING_V * 2,
    thumbTotalHeight + RESOURCE_CARD_PADDING_V * 2,
    34,
  );
  const cardHeight = cardInnerHeight;
  const nextY = ensurePageSpace(pdf, currentY, cardHeight);

  pdf.setDrawColor(...COLOR_CARD_BORDER);
  pdf.setFillColor(...COLOR_CARD_BG);
  pdf.rect(PAGE_MARGIN, nextY, contentWidth, cardHeight, 'FD');

  pdf.setDrawColor(...COLOR_CARD_ACCENT);
  pdf.setFillColor(...COLOR_CARD_ACCENT);
  pdf.rect(PAGE_MARGIN, nextY, RESOURCE_ACCENT_WIDTH, cardHeight, 'F');

  if (hasThumb) {
    const thumbStartX = cardInnerStartX;
    const thumbStartY = nextY + (cardHeight - RESOURCE_THUMB_SIZE) / 2;

    pdf.setDrawColor(...COLOR_THUMB_BORDER);
    pdf.setFillColor(...COLOR_THUMB_BG);
    pdf.rect(
      thumbStartX,
      thumbStartY,
      RESOURCE_THUMB_SIZE,
      RESOURCE_THUMB_SIZE,
      'FD',
    );

    try {
      const innerThumbSize =
        RESOURCE_THUMB_SIZE - RESOURCE_THUMB_BORDER_WIDTH * 2;
      const props =
        typeof pdf.getImageProperties === 'function' && thumbDataUrl
          ? pdf.getImageProperties(thumbDataUrl)
          : null;
      const imgW = props && typeof props.width === 'number' ? props.width : 100;
      const imgH =
        props && typeof props.height === 'number' ? props.height : 100;
      const ratio = Math.min(
        innerThumbSize / Math.max(1, imgW),
        innerThumbSize / Math.max(1, imgH),
      );
      const drawW = Math.max(1, imgW * ratio);
      const drawH = Math.max(1, imgH * ratio);
      const drawX =
        thumbStartX +
        RESOURCE_THUMB_BORDER_WIDTH +
        (innerThumbSize - drawW) / 2;
      const drawY =
        thumbStartY +
        RESOURCE_THUMB_BORDER_WIDTH +
        (innerThumbSize - drawH) / 2;

      pdf.addImage(
        thumbDataUrl,
        'PNG',
        drawX,
        drawY,
        drawW,
        drawH,
        undefined,
        'FAST',
      );
    } catch {
      // noop
    }
  }

  let contentY = nextY + RESOURCE_CARD_PADDING_V;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(titleFontSize);
  pdf.setTextColor(...COLOR_TITLE);
  pdf.text(titleLines, textStartX, contentY);
  contentY += titleLines.length * titleLineHeight + RESOURCE_TITLE_GAP;

  if (descriptionLines.length > 0) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(descriptionFontSize);
    pdf.setTextColor(...COLOR_DESCRIPTION);
    pdf.text(descriptionLines, textStartX, contentY);
    contentY +=
      descriptionLines.length * descriptionLineHeight +
      RESOURCE_DESCRIPTION_GAP;
  }

  if (sourceLines.length > 0) {
    contentY += RESOURCE_SOURCE_GAP;

    const labelPrefix = 'Source:';
    const sourceLinesBaseline = contentY;
    const linkUrl = (resource.url || '').trim();

    for (let lineIndex = 0; lineIndex < sourceLines.length; lineIndex += 1) {
      const line = sourceLines[lineIndex];
      const lineBaseline = sourceLinesBaseline + lineIndex * sourceLineHeight;

      let lineStartX = textStartX;
      let lineContent = line;
      let labelWidth = 0;
      let isFirstLineWithLabel = false;

      if (lineIndex === 0 && line.startsWith(labelPrefix)) {
        isFirstLineWithLabel = true;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(sourceFontSize);
        pdf.setTextColor(...COLOR_SOURCE_LABEL);
        const labelText = `${labelPrefix} `;
        labelWidth = pdf.getTextWidth(labelText);
        pdf.text(labelText, lineStartX, lineBaseline);
        lineStartX += labelWidth + RESOURCE_LABEL_GAP;
        lineContent = line.slice(labelPrefix.length).trimStart();
      }

      if (!lineContent) {
        continue;
      }

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(sourceFontSize);
      pdf.setTextColor(...COLOR_SOURCE_LINK);
      pdf.text(lineContent, lineStartX, lineBaseline);

      const lineWidth = pdf.getTextWidth(lineContent);
      const underlineY = lineBaseline + RESOURCE_LINK_UNDERLINE_OFFSET;
      pdf.setDrawColor(...COLOR_SOURCE_LINK);
      pdf.setLineWidth(0.2);
      pdf.line(lineStartX, underlineY, lineStartX + lineWidth, underlineY);

      if (linkUrl) {
        try {
          pdf.link(
            lineStartX,
            lineBaseline - sourceLineHeight * 0.8,
            lineWidth,
            sourceLineHeight,
            { url: linkUrl },
          );
        } catch {
          // noop
        }
      }

      void isFirstLineWithLabel;
    }
  }

  return nextY + cardHeight;
};

const buildChatTranscriptPdf = async ({
  messages,
  conversationStartedAt,
  imageCache,
}: Pick<ExportChatTranscriptOptions, 'messages' | 'conversationStartedAt'> & {
  imageCache: ResourceImageCache;
}) => {
  const pdf = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: 'a4',
  });

  const pdfTitleTimestamp = formatChatConversationStartedAt(
    conversationStartedAt,
    PDF_TITLE_DATE_FORMAT,
  );
  const pdfTitle = pdfTitleTimestamp
    ? `CareMappr chat conversation - ${pdfTitleTimestamp}`
    : 'CareMappr chat conversation';

  let currentY = PAGE_MARGIN;

  currentY = addWrappedText(pdf, pdfTitle, currentY, {
    fontSize: 16,
    fontStyle: 'bold',
    textColor: [38, 83, 116],
  });

  if (conversationStartedAt) {
    currentY += 1;
    const subtitleTimestamp = formatChatConversationStartedAt(
      conversationStartedAt,
      PDF_SUBTITLE_DATE_FORMAT,
    );
    const subtitle = subtitleTimestamp
      ? `Here is your chat conversation on ${subtitleTimestamp}`
      : 'Here is your chat conversation';
    currentY = addWrappedText(pdf, subtitle, currentY, {
      fontSize: 10,
      textColor: [79, 79, 79],
    });
  }

  currentY += HEADER_GAP;

  if (!messages.length) {
    addWrappedText(pdf, 'No chat messages available.', currentY, {
      fontSize: 11,
    });
    return pdf;
  }

  for (const message of messages) {
    const speaker =
      message.role === 'assistant' ? getAssistantName(message) : 'You';
    const resources = getMessageResources(message);

    currentY = ensurePageSpace(pdf, currentY, 20);
    currentY = addWrappedText(
      pdf,
      `${speaker} - ${message.createdAt}`,
      currentY,
      {
        fontSize: 11,
        fontStyle: 'bold',
        textColor: [38, 83, 116],
      },
    );

    currentY += 1;
    currentY = addWrappedText(
      pdf,
      normalizeMessageText(message.text || ''),
      currentY,
      {
        fontSize: 11,
        textColor: [51, 51, 51],
      },
    );

    if (resources.length > 0) {
      currentY += 1;
      currentY = addWrappedText(pdf, 'Resources', currentY, {
        fontSize: 10,
        fontStyle: 'bold',
        textColor: [79, 79, 79],
      });

      for (const resource of resources) {
        currentY += 1;
        currentY = addResourceCard(pdf, resource, currentY, imageCache);
      }
    }

    currentY += MESSAGE_GAP;
  }

  return pdf;
};

const openPrintDialog = async (pdf: jsPDF) => {
  const blob = pdf.output('blob');
  const blobUrl = URL.createObjectURL(blob);

  await new Promise<void>((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.right = '0';
    iframe.style.bottom = '0';

    const cleanup = () => {
      window.setTimeout(() => {
        iframe.remove();
        URL.revokeObjectURL(blobUrl);
      }, 1000);
    };

    iframe.onload = () => {
      window.setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          resolve();
        } catch (error) {
          reject(error);
        } finally {
          cleanup();
        }
      }, 250);
    };

    iframe.onerror = () => {
      cleanup();
      reject(new Error('Failed to open print dialog'));
    };

    iframe.src = blobUrl;
    document.body.appendChild(iframe);
  });
};

export const exportChatTranscript = async ({
  messages,
  conversationStartedAt,
  shouldDownload = false,
  shouldPrint = false,
}: ExportChatTranscriptOptions) => {
  if (!shouldDownload && !shouldPrint) {
    return;
  }

  const allResources = collectAllResources(messages);
  const imageCache = await preloadResourceImages(allResources);

  const pdf = await buildChatTranscriptPdf({
    messages,
    conversationStartedAt,
    imageCache,
  });
  const fileName = createFileName() || DEFAULT_FILE_NAME;

  if (shouldDownload) {
    pdf.save(fileName);
  }

  if (shouldPrint) {
    await openPrintDialog(pdf);
  }
};
